/**
 * /cgu-garage — Conditions d'accès des garages partenaires (2026-09-17).
 *
 * Acceptation OBLIGATOIRE à l'inscription garage : c'est le document qui rend
 * opposable l'interdiction de réutiliser les coordonnées d'un vendeur. Sans
 * lui, la promesse faite au vendeur dans la politique de confidentialité
 * (« le garage s'engage à ne pas réutiliser vos coordonnées ») ne repose sur
 * rien de contractuel.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import Page from '../components/Layout/Page';

// 2026-10-22 : retrait de la ligne sur les « crédits inclus dans un
// abonnement » — le Garage Pass n'est plus vendu, les crédits s'achètent ou
// s'attribuent à l'unité. Rien d'autre ne change.
const UPDATED = '22 octobre 2026';

const INTERDITS = [
  ['Prospection au-delà de l’offre', 'Les coordonnées obtenues servent à formuler une offre de rachat sur CE véhicule, et à rien d’autre. Aucune relance commerciale sur un autre sujet, aucune inscription à une newsletter, aucun démarchage pour un service annexe.'],
  ['Revente ou cession', 'Les coordonnées ne peuvent être vendues, cédées, échangées ni transmises à un tiers, y compris à une autre société du même groupe, à un mandataire ou à un partenaire financier.'],
  ['Réutilisation ultérieure', 'Passé le refus du vendeur ou la vente du véhicule, les coordonnées ne peuvent plus être utilisées. Elles ne rejoignent aucun fichier de prospection.'],
  ['Constitution de base', 'Aucun export, aucune agrégation, aucune conservation systématique des fiches consultées n’est autorisée, qu’elles aient été déverrouillées ou non.'],
];

export default function CGUGaragePage() {
  return (
    <Page largeur="etroite" titre="Conditions garages partenaires">
      <div className="zk-texte">
        <p className="zk-texte-date">Dernière mise à jour : {UPDATED}</p>

        <h2>1. Objet</h2>
        <p>
          Ces conditions régissent l’accès des garages, concessions et professionnels de l’automobile aux demandes de
          vente déposées par des particuliers sur ZENKART. Leur acceptation est <strong>obligatoire à l’inscription</strong> :
          sans elle, aucun accès n’est ouvert.
        </p>

        <h2>2. Conditions d’accès</h2>
        <ul>
          <li><strong>SIRET vérifié</strong> auprès du répertoire officiel des entreprises.</li>
          <li><strong>Validation manuelle</strong> par ZENKART. Un SIRET valide prouve qu’une entreprise existe, pas qu’elle
            sera sérieuse avec un particulier : nous nous réservons le droit de refuser ou de retirer un accès, sans avoir
            à motiver notre décision.</li>
          <li>Compte nominatif, non partagé, non transférable.</li>
        </ul>

        <h2>3. Ce que vous voyez, et ce que vous ne voyez pas</h2>
        <p>
          Les fiches sont <strong>anonymes</strong> : véhicule, kilométrage, état, photos, secteur géographique approximatif,
          estimation. Elles ne contiennent <strong>ni nom, ni téléphone, ni email, ni adresse</strong>. La description rédigée
          par le vendeur est expurgée automatiquement de tout numéro, email ou lien.
        </p>
        <p>
          Les coordonnées d’un vendeur ne sont transmises qu’après un <strong>déverrouillage</strong> explicite de votre part,
          décompté de vos crédits. Le vendeur est <strong>informé par email</strong> de votre identité au moment où vous
          obtenez ses coordonnées.
        </p>

        <h2>4. Usage des coordonnées — engagements du garage</h2>
        <p>En déverrouillant une fiche, vous vous engagez, sans réserve :</p>
        <ul>
          {INTERDITS.map(([titre, texte]) => <li key={titre}><strong>{titre}</strong> : {texte}</li>)}
        </ul>
        <p>
          Vous agissez comme <strong>responsable de traitement</strong> pour les données que vous recevez, et vous répondez
          seul du respect du RGPD dans votre usage : information de la personne, durée de conservation, droits d’accès et
          d’effacement.
        </p>

        <h2>5. Sanctions</h2>
        <p>
          Tout manquement au point 4 entraîne la <strong>suspension immédiate</strong> de l’accès, sans remboursement des
          crédits restants, et peut donner lieu à signalement à la CNIL. Les déverrouillages étant journalisés (date,
          garage, véhicule), un usage détourné est traçable.
        </p>

        <h2>6. Crédits et facturation</h2>
        <ul>
          <li>Un déverrouillage consomme un crédit, quel que soit le résultat de votre offre.</li>
          <li>Un véhicule déjà déverrouillé par vos soins reste consultable sans nouveau débit.</li>
          <li>Les crédits achetés à l’unité restent acquis tant que le compte est actif.</li>
          <li>Aucun remboursement n’est dû si le vendeur ne répond pas, a déjà vendu, ou refuse votre offre : le service
            rendu est la mise en relation, pas la conclusion d’une vente.</li>
        </ul>

        <h2>7. Absence de garantie sur les véhicules</h2>
        <p>
          ZENKART ne vend pas de véhicules et n’en garantit ni l’état, ni le kilométrage, ni la situation administrative.
          Les estimations affichées sont statistiques et ne valent pas expertise. Il vous appartient de vérifier le
          véhicule avant tout engagement.
        </p>

        <h2>8. Résiliation</h2>
        <p>
          Vous pouvez fermer votre compte à tout moment. Nous pouvons retirer un accès en cas de manquement, de cessation
          d’activité, ou de SIRET devenu invalide. Les obligations du point 4 survivent à la fin de l’accès.
        </p>

        <nav className="zk-texte-liens" aria-label="Documents légaux">
          <Link to="/cgu" className="zk-lien">Conditions d’utilisation</Link>
          <Link to="/confidentialite" className="zk-lien">Politique de confidentialité</Link>
          <Link to="/mentions-legales" className="zk-lien">Mentions légales</Link>
        </nav>
      </div>
    </Page>
  );
}
